<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { base64ToFile } from "@/common/utils/files";
import { getDate } from "@/common/utils/dates";

import {
  equipmentAdd,
  equipmentDel,
  equipmentList,
  equipmentModelCSV,
  equipmentUpload,
} from "@/common/api/rest/equipment";

import type { FileType } from "@/common/types/common";
import type {
  EquipmentListType,
  EquipmentPayloadType,
} from "@/common/types/equipment";

import { RegisterTheme } from "@/common/themes";
import { equipmentForm, fileUploadForm } from "@/components";
import {
  baseAvatar,
  baseConfirm,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t, tm } = useI18n();
const route = useRoute();
const router = useRouter();

const accountId = computed(() => route.params.id as string);

const tableHeader = computed(() => [
  { key: "avatar", label: null },
  { key: "code", label: t("common.labels.code") },
  { key: "name", label: t("common.labels.name") },
  { key: "status", label: t("common.labels.status") },
  { key: "calibration_date", label: t("common.labels.calibration_date") },
  { key: "next_calibration", label: t("common.labels.next_calibration") },
]);

const tableData = ref<EquipmentListType[]>([]);

const selected = ref<EquipmentListType | null>(null);
const payload = ref<EquipmentPayloadType | null>(null);

const fileUpload = ref<FileType | null>(null);
const fileExtensions = ref(["csv"]);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModal = ref(false);
const showModalUpload = ref(false);
const showConfirmDel = ref(false);

const ItemStatusEnum = computed(
  () => tm("enums.item_status") as Record<string, string>,
);

function fetchData() {
  equipmentList(accountId.value, {
    limit: limit.value,
    page: page.value,
    search: search.value,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        tableData.value = data.list;
        page.value = data.infos.page;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updatedPayload(data: Partial<EquipmentPayloadType>) {
  payload.value = { ...payload.value, ...data } as any;
}

function add() {
  showModal.value = true;
  payload.value = null;
}

function addCancel() {
  showModal.value = false;
  payload.value = null;
}

function addConfirm() {
  if (!payload.value) return;

  store.commit("SET_LOADING", true);
  showModal.value = false;

  equipmentAdd(accountId.value, payload.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        toast({ message: message, type: "success" });
        router.push(
          `/${accountId.value}/register/equipment/edit/${data.equipment_id}`,
        );
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function edit(data: EquipmentListType) {
  router.push(
    `/${accountId.value}/register/equipment/edit/${data.equipment_id}`,
  );
}

function remove(data: EquipmentListType) {
  selected.value = data;
  showConfirmDel.value = true;
}

function removeCancel() {
  selected.value = null;
  showConfirmDel.value = false;
}

function removeConfirm() {
  if (!selected.value) return;

  store.commit("SET_LOADING", true);
  showConfirmDel.value = false;

  equipmentDel(accountId.value, selected.value.equipment_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        removeCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function searchItem(value: string) {
  page.value = 0;
  search.value = value;
  fetchData();
}

function changePage(value: number) {
  page.value = value;
  fetchData();
}

function uploadCsv() {
  showModalUpload.value = true;
}

function uploadCancel() {
  showModalUpload.value = false;
  fileUpload.value = null;
}

function uploadConfirm() {
  if (!fileUpload.value) return;

  showModalUpload.value = false;
  store.commit("SET_LOADING", true);

  equipmentUpload(accountId.value, fileUpload.value)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
        uploadCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function downloadModel() {
  equipmentModelCSV(accountId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        base64ToFile(data.file, data.name, data.type);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

onMounted(() => {
  fetchData();
});
</script>

<template>
  <register-theme tab-active="equipment">
    <template #title>
      <svg-icon icon="equipment" size="2rem" />
      {{ t("common.labels.equipments") }}
    </template>

    <template #content>
      <base-table
        :header="tableHeader"
        :data="tableData"
        :limit="limit"
        :page="page"
        :pages="pages"
        :total="total"
        :searched="searched"
        :options="true"
        :edit="true"
        :del="true"
        :upload="true"
        @change:page="changePage"
        @searching="searchItem"
        @btn:add="add()"
        @btn:edit="edit"
        @btn:del="remove"
        @btn:upload="uploadCsv()"
      >
        <template #avatar="{ data }">
          <base-avatar
            :url="data.avatar?.small"
            size="1.75rem"
            background="#ffffff"
          />
        </template>

        <template #status="{ data }">
          {{ ItemStatusEnum[data.status] }}
        </template>

        <template #calibration_date="{ data }">
          {{ getDate(data.calibration_date) }}
        </template>

        <template #next_calibration="{ data }">
          {{ getDate(data.next_calibration) }}
        </template>
      </base-table>

      <base-modal
        :show="showModal"
        :title="
          t('common.labels.add', {
            concat: t('common.labels.equipment'),
          }).toUpperCase()
        "
        :show-button-confirm="true"
        :button-confirm="t('common.labels.add')"
        :button-close="t('common.labels.cancel')"
        @close="addCancel()"
        @confirm="addConfirm()"
      >
        <template #content>
          <equipment-form
            :data="payload"
            :is-patch="false"
            @update-data="updatedPayload"
          />
        </template>
      </base-modal>

      <base-modal
        :show="showModalUpload"
        :title="
          t('common.labels.upload', {
            concat: t('common.labels.equipment'),
          }).toUpperCase()
        "
        :show-button-confirm="true"
        :button-confirm="t('common.labels.send')"
        :button-close="t('common.labels.cancel')"
        @close="uploadCancel()"
        @confirm="uploadConfirm()"
      >
        <template #content>
          <file-upload-form
            :extensions="fileExtensions"
            :model="true"
            @update-data="fileUpload = $event"
            @model-download="downloadModel()"
          />
        </template>
      </base-modal>

      <base-confirm
        :show="showConfirmDel"
        :title="
          t('common.labels.remove', {
            concat: t('common.labels.sample'),
          }).toUpperCase()
        "
        :message="
          t('common.texts.remove_message', {
            concat: selected?.code,
          })
        "
        @cancel="removeCancel"
        @confirm="removeConfirm"
      />
    </template>
  </register-theme>
</template>
